"use client";

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { fetchWithAuth } from "@/lib/fetchWithAuth";

type Format = 'xlsx' | 'pdf';

const POPOVER_WIDTH = 340;
const POPOVER_APPROX_HEIGHT = 360;

export function ArtifactDownloadModal({
  anchorRect,
  onClose,
  artifactId,
  type,
  title,
  xmlContent,
  hasAttachments,
}: {
  anchorRect: DOMRect | null;
  onClose: () => void;
  artifactId?: string | null;
  type: string;
  title: string;
  xmlContent: string;
  hasAttachments: boolean;
}) {
  const [format, setFormat] = useState<Format>('xlsx');
  const [includeImages, setIncludeImages] = useState(true);
  const [includeSignature, setIncludeSignature] = useState(false);
  const [includeRiskGrade, setIncludeRiskGrade] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSignature, setHasSignature] = useState<boolean | null>(null); // null = 아직 조회 중
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (anchorRect) {
      setError(null);
      setSubmitting(false);
    }
  }, [anchorRect]);

  // 모달이 열릴 때 사용자 서명 보유 여부 조회
  useEffect(() => {
    if (!anchorRect) return;
    let cancelled = false;
    const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
    fetchWithAuth(`${apiUrl}/api/users/me`)
      .then(res => (res.ok ? res.json() : null))
      .then(data => {
        if (cancelled || !data) return;
        const profile = data.data ?? data;
        const has = !!(profile.signature_file_id ?? profile.signatureFileId);
        setHasSignature(has);
        setIncludeSignature(has); // 있으면 기본 on, 없으면 off
      })
      .catch(() => { if (!cancelled) setHasSignature(false); });
    return () => { cancelled = true; };
  }, [anchorRect]);

  // ESC + 바깥 클릭으로 닫기
  useEffect(() => {
    if (!anchorRect) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    const onDocMouseDown = (e: MouseEvent) => {
      if (submitting) return;
      const target = e.target as Node;
      // 팝오버 내부 클릭은 무시. 앵커 버튼 본체에 대한 클릭은 토글 역할이므로 닫지 않음(버튼이 다시 토글함)
      if (popoverRef.current && !popoverRef.current.contains(target)) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    // mousedown 을 쓰면 앵커 버튼 onClick(마우스업) 직전에 먼저 닫혀 토글이 꼬이므로 click 사용
    document.addEventListener('click', onDocMouseDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('click', onDocMouseDown);
    };
  }, [anchorRect, submitting, onClose]);

  if (!anchorRect) return null;

  // 위치 계산 — 버튼 아래 오른쪽 정렬. 아래 여백 부족 시 위로 뒤집기.
  const spaceBelow = (typeof window !== 'undefined' ? window.innerHeight : 1000) - anchorRect.bottom;
  const placement: 'bottom' | 'top' = spaceBelow > POPOVER_APPROX_HEIGHT ? 'bottom' : 'top';
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : 1000;
  const right = Math.max(8, viewportW - anchorRect.right);
  const style: React.CSSProperties =
    placement === 'bottom'
      ? { position: 'fixed', top: anchorRect.bottom + 8, right, width: POPOVER_WIDTH, zIndex: 200 }
      : { position: 'fixed', bottom: (typeof window !== 'undefined' ? window.innerHeight : 1000) - anchorRect.top + 8, right, width: POPOVER_WIDTH, zIndex: 200 };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const apiUrl = process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3000';
      const storedArtifactId = artifactId?.trim() && /^\d+$/.test(artifactId)
        ? artifactId
        : null;
      const res = storedArtifactId
        ? await fetchWithAuth(
            `${apiUrl}/api/artifacts/${encodeURIComponent(storedArtifactId)}/download?${new URLSearchParams({
              format,
              includeImages: String(includeImages),
              includeSignature: String(includeSignature),
              includeRiskGrade: String(includeRiskGrade),
            }).toString()}`,
          )
        : await fetchWithAuth(`${apiUrl}/api/artifacts/download`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ type, title, content: xmlContent, format, includeImages, includeSignature, includeRiskGrade }),
          });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        let detail = '';
        try {
          const payload = JSON.parse(txt);
          detail = payload?.error?.message || payload?.message || '';
        } catch {
          detail = txt;
        }
        throw new Error(detail || `다운로드 실패 (${res.status})`);
      }
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const suggestedName = match ? decodeURIComponent(match[1]) : `${title || '위험성평가'}.${format}`;

      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggestedName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      onClose();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : '알 수 없는 오류가 발생했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const node = (
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="다운로드 설정"
      style={style}
      className="bg-white dark:bg-[#161618] border border-gray-200 dark:border-[#2a2a2e] rounded-[18px] shadow-[0_12px_40px_rgba(0,0,0,0.15)] dark:shadow-[0_12px_40px_rgba(0,0,0,0.5)] overflow-hidden animate-in fade-in zoom-in-95 duration-150"
    >
      <div className="px-5 py-4 border-b border-gray-100 dark:border-[#2a2a2e]">
        <h3 className="text-[14px] font-black text-gray-900 dark:text-gray-100 tracking-tight">다운로드 설정</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 font-medium mt-0.5">저장 형식과 포함할 내용을 선택하세요.</p>
      </div>

      <div className="px-5 py-4 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <span className="text-[12px] font-bold text-gray-700 dark:text-gray-300">저장 형식</span>
          <div className="grid grid-cols-2 gap-1.5">
            {(['xlsx', 'pdf'] as Format[]).map(f => {
              const selected = format === f;
              return (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFormat(f)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-[12px] font-bold transition-all ${
                    selected
                      ? 'border-seexr-primary bg-seexr-solid/10 text-seexr-primary dark:text-seexr-400'
                      : 'border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-[#3a3a3e]'
                  }`}
                >
                  <span className={`w-3.5 h-3.5 rounded-full border-[4px] shrink-0 ${selected ? 'border-seexr-primary' : 'border-gray-300 dark:border-[#3a3a3e]'}`} />
                  <span className="flex-1 text-left">{f === 'xlsx' ? 'Excel' : 'PDF'}</span>
                </button>
              );
            })}
          </div>
          {format === 'pdf' && (
            <p className="text-[10.5px] text-gray-400 font-medium leading-snug">※ PDF는 서버에서 Excel을 변환하므로 약간 느립니다.</p>
          )}
        </div>

        <ToggleRow
          label="개선 전·후 위험성 결정 체크 포함"
          description="개선 전·후 상/중/하 칸에 자동 판정된 ✓ 를 찍습니다. 끄면 전부 공란으로 추출됩니다."
          checked={includeRiskGrade}
          disabled={submitting}
          onChange={setIncludeRiskGrade}
        />

        <ToggleRow
          label="현장 사진 포함"
          description={hasAttachments ? "별도 시트로 첨부 사진을 추가합니다." : "첨부된 사진이 없습니다."}
          checked={includeImages}
          disabled={!hasAttachments || submitting}
          onChange={setIncludeImages}
        />

        <ToggleRow
          label="내 서명 포함"
          description={
            hasSignature === false
              ? "등록된 서명·도장이 없습니다. 설정에서 먼저 등록해 주세요."
              : "프로필에 등록된 서명·도장을 담당자 칸에 삽입합니다."
          }
          checked={includeSignature && hasSignature === true}
          disabled={submitting || hasSignature !== true}
          onChange={setIncludeSignature}
        />

        {error && (
          <div className="text-[11px] font-bold px-3 py-2 rounded-lg bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400 border border-red-100 dark:border-red-900/40 leading-snug">
            {error}
          </div>
        )}
      </div>

      <div className="px-5 py-3 border-t border-gray-100 dark:border-[#2a2a2e] flex items-center justify-end gap-2 bg-gray-50/50 dark:bg-[#141416]">
        <button
          type="button"
          onClick={onClose}
          disabled={submitting}
          className="px-3.5 py-1.5 rounded-full border border-gray-200 dark:border-[#2a2a2e] bg-white dark:bg-[#1a1a1c] text-gray-700 dark:text-gray-300 text-[12px] font-bold hover:bg-gray-50 dark:hover:bg-[#1f1f22] transition-all disabled:opacity-40"
        >
          취소
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="px-3.5 py-1.5 rounded-full bg-gray-900 dark:bg-white text-white dark:text-gray-900 text-[12px] font-bold shadow-sm hover:bg-black dark:hover:bg-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
        >
          {submitting ? (
            <><span className="w-3.5 h-3.5 border-2 border-white/30 dark:border-gray-900/30 border-t-white dark:border-t-gray-900 rounded-full animate-spin" /> 생성 중…</>
          ) : (
            <>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
              저장
            </>
          )}
        </button>
      </div>
    </div>
  );

  return typeof document !== 'undefined' ? createPortal(node, document.body) : null;
}

function ToggleRow({
  label, description, checked, disabled, onChange,
}: {
  label: string;
  description?: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <div className={`flex items-start justify-between gap-3 ${disabled ? 'opacity-50' : ''}`}>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold text-gray-800 dark:text-gray-200">{label}</div>
        {description && <div className="text-[10.5px] text-gray-400 font-medium mt-0.5 leading-snug">{description}</div>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative shrink-0 w-9 h-5 rounded-full transition-colors outline-none focus-visible:ring-2 focus-visible:ring-seexr-primary/50 ${
          checked ? 'bg-seexr-solid' : 'bg-gray-200 dark:bg-[#2a2a2e]'
        } ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
      >
        <span
          className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-4' : 'translate-x-0'
          }`}
        />
      </button>
    </div>
  );
}
